import { Alert, Box, Button, Skeleton, Stack } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCreateSampleProject, useDeleteProject, useProjects } from '@/api';
import { MaterialSymbol } from '@/components/MaterialSymbol';
import { NewProjectDialog, ProjectTable } from '@/components/portfolio';
import { EmptyState, PageHeader } from '@/components/ui';
import { useSampleProjectEnabled } from '@/lib';

/**
 * The project portfolio — this module's front door, and the page a project is
 * reached through.
 *
 * It replaced the POC scaffold that stood here (an identity debug card, an
 * inline create form and a plan-vs-budget card). The identity block is in the
 * sidebar; plan-vs-budget is on Planning, where the plan it measures is. What
 * is left is what TimeLog's own Project Portfolio is: a header, one primary
 * action, and the list. See ADR 0039.
 */
export const ProjectsPage = () => {
  const { t } = useTranslation('pmPortfolio');
  const [dialogOpen, setDialogOpen] = useState(false);
  const { data, isPending, error } = useProjects();
  const deleteProject = useDeleteProject();
  const createSample = useCreateSampleProject();
  const sampleEnabled = useSampleProjectEnabled();

  const projects = data?.projects ?? [];

  return (
    <Stack gap={2} data-automation-id="projects-page">
      <PageHeader
        title={t('page.Title')}
        icon="view_object_track"
        actions={
          <Stack direction="row" gap={1}>
            {/*
              Off by default: a button that conjures a fabricated project does
              not belong beside someone's real ones. The flow behind it is
              untouched, and the sidebar's dev toggle turns it back on without a
              dev-server restart — see `lib/sampleProject.ts`.
            */}
            {sampleEnabled ? (
              <Button
                variant="outlined"
                color="secondary"
                disabled={createSample.isPending}
                onClick={() => createSample.mutate(undefined)}
                data-automation-id="create-sample-project-button"
              >
                {t('page.actions.CreateSample')}
              </Button>
            ) : null}
            <Button
              variant="contained"
              startIcon={<MaterialSymbol name="add" size={16} />}
              onClick={() => setDialogOpen(true)}
              data-automation-id="new-project-button"
            >
              {t('page.actions.NewProject')}
            </Button>
          </Stack>
        }
      />

      {error ? <Alert severity="error">{error.message}</Alert> : null}
      {createSample.error ? <Alert severity="error">{createSample.error.message}</Alert> : null}

      {/*
        The header row and the card are ONE child, not two: `PanelContent` wraps
        its children in a `Stack gap={2}`, so a header and a card passed
        separately collect that gap on top of the header's own margin and the
        section sits ~16px lower than the same section elsewhere.
      */}
      <Box
        sx={{
          bgcolor: 'var(--bg-panel)',
          borderRadius: 'var(--border-radius-medium)',
          border: 'var(--border-width-hairline) solid var(--border-subtle)',
          overflow: 'hidden',
        }}
      >
        {isPending ? <Skeleton variant="rectangular" height={160} /> : null}

        {data && projects.length === 0 ? (
          <EmptyState
            automationId="projects-empty"
            icon="view_object_track"
            title={t('emptyState.Title')}
            body={t('emptyState.Body')}
          >
            <Button
              variant="contained"
              startIcon={<MaterialSymbol name="add" size={17} />}
              onClick={() => setDialogOpen(true)}
              data-automation-id="empty-new-project-button"
              sx={{ px: 2, py: 1 }}
            >
              {t('page.actions.NewProject')}
            </Button>
          </EmptyState>
        ) : null}

        {projects.length > 0 ? (
          <ProjectTable projects={projects} onDelete={(id) => deleteProject.mutate(id)} />
        ) : null}
      </Box>

      <NewProjectDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </Stack>
  );
};
